import React, { useState, useCallback, useEffect } from "react"
import { Form, Button, Row, Col, Card, Spinner } from "react-bootstrap"
import { useNavigate, Link, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import useAuthAxios from "../hooks/useAuthAxios"
import Footer from "../components/layout/Footer"
import logo from "../assets/images/logo.jpg"
import { useAuth } from "../context/AuthContext"
import { validatePassword } from "../utils/validationUtils"

const PasswordReset = () => {
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [passwordValidation, setPasswordValidation] = useState({ isValid: false, errors: [] })
  const [confirmPasswordError, setConfirmPasswordError] = useState("")
  const navigate = useNavigate()
  const { uid } = useParams()
  const { post } = useAuthAxios()
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/")
    }
  }, [isAuthenticated, navigate])

  // Handle password input change with validation
  const handlePasswordChange = e => {
    const newPassword = e.target.value
    setPassword(newPassword)

    const validation = validatePassword(newPassword)
    setPasswordValidation(validation)

    // Also validate confirm password if it exists
    if (confirmPassword) {
      const confirmValidation = validateConfirmPassword(newPassword, confirmPassword)
      setConfirmPasswordError(confirmValidation.error)
    }
  }

  // Handle confirm password input change with validation
  const handleConfirmPasswordChange = e => {
    const newConfirmPassword = e.target.value
    setConfirmPassword(newConfirmPassword)

    const validation = validateConfirmPassword(password, newConfirmPassword)
    setConfirmPasswordError(validation.error)
  }

  // Confirm password validation function
  const validateConfirmPassword = (password, confirmPassword) => {
    if (!confirmPassword) {
      return { isValid: false, error: "Η επιβεβαίωση κωδικού είναι υποχρεωτική" }
    }
    if (password !== confirmPassword) {
      return { isValid: false, error: "Οι κωδικοί δεν ταιριάζουν" }
    }
    return { isValid: true, error: "" }
  }

  const handleSubmit = useCallback(
    async e => {
      e.preventDefault()

      // Validate password before submission
      if (!passwordValidation.isValid) {
        toast.error("Παρακαλώ διορθώστε τον κωδικό πρόσβασης: " + passwordValidation.errors.join(", "))
        return
      }

      setIsSubmitting(true)

      try {
        const response = await post("/password-reset", { uid, password })

        if (response.success) {
          setIsSubmitted(true)
        }
      } catch (error) {
        toast.error("Παρουσιάστηκε σφάλμα κατά την αλλαγή του κωδικού.")
      } finally {
        setIsSubmitting(false)
      }
    },
    [uid, password, post, passwordValidation]
  )

  if (isSubmitted) {
    return (
      <div className="grid-background registration-page">
        <div className="min-vh-100 d-flex flex-column registration-page">
          <div className="flex-grow-1 d-flex align-items-center py-5">
            <div className="container-fluid">
              <Row className="justify-content-center">
                <Col xs={12} md={8} lg={6} xl={5}>
                  <div className="text-center mb-4">
                    <Link to="/">
                      <img src={logo} alt="eGarages logo" className="registration-logo" />
                    </Link>
                  </div>
                  <Card className="content-glass">
                    <Card.Body className="p-4 text-center">
                      <div className="mb-4">
                        <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
                        <h4 className="mb-3">Επιτυχής Επαναφορά Κωδικού</h4>
                        <p className="text-muted">
                          Ο κωδικός σας άλλαξε επιτυχώς. Πατώντας τον ακόλουθο σύνδεσμο, μπορείτε να
                          μεταφερθείτε στη σελίδα σύνδεσης.
                        </p>
                      </div>
                      <div className="d-grid">
                        <Link to="/login">Μετάβαση στη Σύνδεση</Link>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    )
  }

  return (
    <div className="grid-background registration-page">
      <div className="min-vh-100 d-flex flex-column registration-page">
        <div className="flex-grow-1 d-flex align-items-center py-5">
          <div className="container-fluid">
            <Row className="justify-content-center">
              <Col xs={12} md={8} lg={6} xl={5}>
                <div className="text-center mb-4">
                  <Link to="/">
                    <img src={logo} alt="eGarages logo" className="registration-logo" />
                  </Link>
                </div>
                <Card className="content-glass">
                  <Card.Header>
                    <h4 className="mb-0 text-center">Επαναφορά Κωδικού</h4>
                  </Card.Header>
                  <Card.Body className="p-4">
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-4">
                        <Form.Label>Νέος Κωδικός</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Εισάγετε τον νέο κωδικό σας"
                          value={password}
                          onChange={handlePasswordChange}
                          isInvalid={password.length > 0 && !passwordValidation.isValid}
                          required
                        />
                        <Form.Control.Feedback type="invalid">
                          {passwordValidation.errors.map((error, index) => (
                            <div key={index}>{error}</div>
                          ))}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label>Επιβεβαίωση Νέου Κωδικού</Form.Label>
                        <Form.Control
                          type="password"
                          placeholder="Επαναλάβετε τον νέο κωδικό σας"
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                          isInvalid={confirmPassword.length > 0 && confirmPasswordError}
                          required
                        />
                        <Form.Control.Feedback type="invalid">{confirmPasswordError}</Form.Control.Feedback>
                      </Form.Group>

                      <div className="d-grid mb-3">
                        <Button
                          type="submit"
                          variant="primary"
                          size="lg"
                          disabled={
                            isSubmitting ||
                            !passwordValidation.isValid ||
                            !confirmPassword ||
                            confirmPasswordError
                          }>
                          {isSubmitting ? <Spinner animation="border" size="sm" /> : "Αλλαγή Κωδικού"}
                        </Button>
                      </div>

                      <div className="text-center">
                        <Link to="/login">Επιστροφή στη Σύνδεση</Link>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  )
}

export default PasswordReset
